/**
 * Shared time-window state for every time-series chart in the portal (salience
 * ShareTrendChart, the home cross-signal chart, and — later — EngagementChart).
 *
 * This is the logic that was copy-pasted three ways: range presets, drag-to-
 * select-a-window, ◀▶ paging, a custom-zoom override, and reset. Owning it once
 * means a feature added here (touch support, smoothing, a new preset) lands in
 * every chart at once — the "update once" goal.
 *
 * The chart still owns its OWN data + rendering; this hook only resolves the
 * visible [winLo, winHi] and the interaction state around it. Charts feed it
 * their data bounds and translate recharts mouse events into ts numbers.
 */
import { useState } from "react";

export const DAY_MS = 86_400_000;

export interface RangePreset {
  key: string;
  label: string;
  /** Window width in days; null = "all" (no width, spans the data). */
  days: number | null;
}

export interface UseTimeWindowOptions {
  ranges: readonly RangePreset[];
  defaultRange?: string;
  /** Earliest/latest ts in the FULL (unwindowed) dataset; 0/0 when empty. */
  dataMinTs: number;
  dataMaxTs: number;
  /**
   * Where the right edge sits when not paged/zoomed:
   *   "dataMax" — the last data point (no trailing empty space) [default]
   *   "now"     — today, even if the latest data is older (shows the gap)
   */
  anchor?: "dataMax" | "now";
  /** A drag shorter than this (days) is a click, not a zoom. Default 2. */
  minZoomDays?: number;
}

export type DragEndResult =
  | { kind: "zoom" }
  | { kind: "click"; ts: number }
  | null;

export interface TimeWindow {
  range: string;
  setRange: (k: string) => void;
  /** Resolved visible window. */
  winLo: number;
  winHi: number;
  span: number;
  isWindowed: boolean;
  isCustom: boolean;
  /** Whether a ◀▶ nav is meaningful (a bounded preset, or a custom zoom). */
  showNav: boolean;
  canBack: boolean;
  canFwd: boolean;
  shift: (dir: -1 | 1) => void;
  reset: () => void;
  /** Drag-to-zoom interaction state + handlers (charts pass ts numbers). */
  dragStart: number | null;
  dragCur: number | null;
  onDragStart: (ts: number) => void;
  onDragMove: (ts: number) => void;
  onDragEnd: () => DragEndResult;
}

function nowMs(): number {
  return Date.now();
}

export function useTimeWindow(opts: UseTimeWindowOptions): TimeWindow {
  const {
    ranges,
    dataMinTs,
    dataMaxTs,
    anchor = "dataMax",
    minZoomDays = 2,
  } = opts;

  const [range, setRangeState] = useState<string>(
    opts.defaultRange ?? ranges[0]?.key ?? "all",
  );
  // endTs anchors the right edge of a preset window (null = default anchor);
  // custom is a drag-selected [lo,hi] zoom that overrides the preset.
  const [endTs, setEndTs] = useState<number | null>(null);
  const [custom, setCustom] = useState<{ lo: number; hi: number } | null>(null);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragCur, setDragCur] = useState<number | null>(null);

  const hasData = dataMaxTs > 0 && dataMaxTs >= dataMinTs;
  const hiBound = anchor === "now" ? Math.max(dataMaxTs, nowMs()) : dataMaxTs;
  const rangeDays = ranges.find((r) => r.key === range)?.days ?? null;

  let winLo: number;
  let winHi: number;
  if (custom) {
    winLo = Math.max(custom.lo, dataMinTs);
    winHi = Math.min(custom.hi, hiBound);
  } else if (rangeDays != null) {
    winHi = Math.min(endTs ?? hiBound, hiBound);
    winLo = winHi - rangeDays * DAY_MS;
  } else {
    winLo = dataMinTs;
    winHi = hiBound;
  }

  const span = Math.max(winHi - winLo, DAY_MS);
  const isCustom = custom != null;
  const isWindowed = custom != null || endTs != null;
  const showNav = rangeDays != null || custom != null;
  const canBack = hasData && winLo > dataMinTs;
  const canFwd = hasData && winHi < hiBound;

  function shift(dir: -1 | 1) {
    let newHi = winHi + dir * span;
    newHi = Math.min(Math.max(newHi, dataMinTs + span), hiBound);
    if (custom) {
      setCustom({ lo: newHi - span, hi: newHi });
    } else {
      setEndTs(newHi >= hiBound ? null : newHi);
    }
  }

  function reset() {
    setCustom(null);
    setEndTs(null);
  }

  function setRange(k: string) {
    setRangeState(k);
    reset();
  }

  function onDragStart(ts: number) {
    setDragStart(ts);
    setDragCur(ts);
  }
  function onDragMove(ts: number) {
    if (dragStart != null) setDragCur(ts);
  }
  function onDragEnd(): DragEndResult {
    let result: DragEndResult = null;
    if (dragStart != null && dragCur != null) {
      const lo = Math.min(dragStart, dragCur);
      const hi = Math.max(dragStart, dragCur);
      if (hi - lo >= minZoomDays * DAY_MS) {
        setCustom({ lo, hi });
        result = { kind: "zoom" };
      } else {
        result = { kind: "click", ts: dragStart };
      }
    }
    setDragStart(null);
    setDragCur(null);
    return result;
  }

  return {
    range,
    setRange,
    winLo,
    winHi,
    span,
    isWindowed,
    isCustom,
    showNav,
    canBack,
    canFwd,
    shift,
    reset,
    dragStart,
    dragCur,
    onDragStart,
    onDragMove,
    onDragEnd,
  };
}
